import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo';
import { fetchBySlug, fetchCollection, optimizeImage, getItemSlug } from '../lib/cms';
import CategoryBadge from '../components/CategoryBadge';
import GalleryLightbox from '../components/GalleryLightbox';
import HelpNotice from '../components/HelpNotice';
import ArticleCard from '../components/ArticleCard';
import ShareMenu from '../components/ShareMenu';

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    let isMounted = true;
    async function loadArticle() {
      setLoading(true);
      try {
        // Try fetching directly by slug
        const decodedSlug = decodeURIComponent(slug || '').trim();
        let item = await fetchBySlug(decodedSlug || slug);

        // If not returned by slug endpoint, fallback to collection search
        if (!item || !item.title || (item.collectionName && item.collectionName !== 'articles')) {
          const listRes = await fetchCollection('articles', { limit: 50 });
          item = (listRes.items || []).find((a) => {
            const aSlug = getItemSlug(a);
            return aSlug === slug || aSlug === decodedSlug || a.id === slug;
          });
        }

        if (isMounted && item) {
          setArticle(item);

          // If article has an attached gallery slug, load it
          if (item.gallery_slug) {
            const galRes = await fetchCollection('galleri', { limit: 10 });
            const foundGal = (galRes.items || []).find((g) => {
              const gSlug = getItemSlug(g);
              return gSlug === item.gallery_slug || g.id === item.gallery_slug;
            });
            if (foundGal) setGallery(foundGal);
          }

          // Load related articles
          const relRes = await fetchCollection('articles', { limit: 4 });
          const related = (relRes.items || [])
            .filter((a) => a.id !== item.id)
            .slice(0, 3);
          setRelatedArticles(related);
        }
      } catch (err) {
        console.warn('Failed to load article detail', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadArticle();
    return () => { isMounted = false; };
  }, [slug]);

  const seoTitle = article?.seo_title || article?.title || 'Artikel';
  const seoDescription = article?.seo_description || article?.intro || '';

  useSeo({
    title: seoTitle,
    description: seoDescription,
  });

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(window.location.href);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-white">
        <div className="font-serif text-lg text-slate-500 flex items-center gap-3">
          <i className="fa-solid fa-spinner fa-spin text-[#fd7302]" aria-hidden="true" />
          <span>Henter artikel...</span>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white px-4 text-center">
        <h1 className="font-serif text-3xl font-bold text-[#010b27]">
          Artiklen blev ikke fundet
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Den efterspurgte artikel findes ikke i redaktionens arkiv eller er midlertidigt afpubliceret.
        </p>
        <Link
          to="/artikler"
          className="mt-6 inline-flex items-center gap-2 rounded-sm bg-[#010b27] px-5 py-2.5 text-xs font-semibold text-white hover:bg-[#fd7302]"
        >
          <i className="fa-solid fa-arrow-left" aria-hidden="true" />
          <span>Tilbage til artikelarkivet</span>
        </Link>
      </div>
    );
  }

  const categoryName = article.terms?.category?.[0]
    ? article.terms.category[0].replace(/-/g, ' ')
    : '';

  const formattedDate = article.publish_date
    ? new Date(article.publish_date).toLocaleDateString('da-DK', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';

  return (
    <article className="min-h-screen bg-white pb-16">
      {/* Breadcrumbs bar */}
      <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-1.5 sm:py-2.5 text-[11px] sm:text-xs text-slate-500">
        <div className="mx-auto max-w-7xl flex items-center gap-1.5 sm:gap-2 overflow-hidden">
          <Link to="/" className="hover:text-[#010b27] shrink-0">Forside</Link>
          <span className="text-slate-400 shrink-0">/</span>
          <Link to="/artikler" className="hover:text-[#010b27] shrink-0">Artikler</Link>
          <span className="text-slate-400 shrink-0">/</span>
          <span className="truncate text-[#010b27] font-medium">
            {article.title}
          </span>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 pt-8 sm:pt-12">
        {/* Category, Audience & Draft pill */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <CategoryBadge
            audience={article.audience}
            category={categoryName}
            size="sm"
          />
          {article.status === 'draft' && (
            <span className="rounded-xs border border-amber-600 bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-800 uppercase tracking-wider">
              Udkast til intern redaktion
            </span>
          )}
        </div>

        {/* Headline */}
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-[2.85rem] font-black text-[#010b27] leading-[1.15] tracking-tight">
          {article.title}
        </h1>

        {/* Subtitle / Underrubrik */}
        {article.subtitle && (
          <p className="mt-4 font-serif text-lg sm:text-xl md:text-2xl text-slate-700 italic leading-snug">
            {article.subtitle}
          </p>
        )}

        {/* Byline & Metadata bar */}
        <div className="mt-6 pt-4 pb-4 border-y border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-slate-500">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#010b27] text-white font-serif font-bold text-sm">
              {article.author ? article.author.charAt(0) : 'R'}
            </div>
            <div>
              <div className="font-bold text-[#010b27]">
                Af {article.author || 'Redaktionen'}
              </div>
              <div className="text-[11px] text-slate-500">
                {article.author_role || 'Journalist & skatteanalytiker'}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs">
            <time dateTime={article.publish_date} className="font-mono">
              <i className="fa-solid fa-calendar-days mr-1.5 text-[#fd7302]" aria-hidden="true" />
              {formattedDate}
            </time>
            {article.reading_time && (
              <span className="font-mono">
                <i className="fa-solid fa-clock mr-1 text-[#fd7302]" aria-hidden="true" />
                {article.reading_time}
              </span>
            )}
            <ShareMenu
              title={article.title}
              subtitle={article.subtitle}
              url={typeof window !== 'undefined' ? window.location.href : ''}
            />
          </div>
        </div>

        {/* Cover Image */}
        {article.cover_image?.[0] && (
          <figure className="my-8">
            <div className="overflow-hidden rounded-md border border-slate-200 bg-slate-50">
              <img
                src={optimizeImage(article.cover_image[0], { width: 1200 })}
                alt={article.title}
                className="w-full aspect-16/9 object-cover"
              />
            </div>
            <figcaption className="mt-2 text-xs text-slate-500 italic flex items-center justify-between">
              <span>Redaktionelt arkivfoto · Skatunderlup</span>
              <span className="font-mono text-[10px]">FOTO: REDAKTIONEN</span>
            </figcaption>
          </figure>
        )}

        {/* Editorial Layout: Main Article Column + Sidebar within max-w-7xl */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 xl:gap-12 items-start">
          {/* Main Article Content */}
          <div className="lg:col-span-8">
            {/* Lead / Intro Paragraph */}
            {article.intro && (
              <div className="my-6 border-l-4 border-[#fd7302] pl-4 sm:pl-6 py-1 bg-slate-50/60 rounded-r-sm">
                <p className="font-serif text-lg sm:text-xl text-[#010b27] leading-relaxed font-semibold">
                  {article.intro}
                </p>
              </div>
            )}

            {/* Main Body (HTML formatted with comprehensive typography styling) */}
            <div
              className="prose article-content max-w-none text-[#1e293b]"
              dangerouslySetInnerHTML={{ __html: article.body || '<p>Ingen brødtekst tilgængelig.</p>' }}
            />

            {/* Attached Gallery Lightbox if present */}
            {gallery && (
              <div className="my-10">
                <GalleryLightbox gallery={gallery} />
              </div>
            )}

            {/* Supplementary article images */}
            {article.article_images && article.article_images.length > 0 && !gallery && (
              <div className="my-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {article.article_images.map((img, i) => (
                  <div key={i} className="overflow-hidden rounded-md border border-slate-200">
                    <img
                      src={optimizeImage(img, { width: 700 })}
                      alt={`Supplerende artikelfoto ${i + 1}`}
                      className="w-full aspect-16/10 object-cover"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Author Byline / Fact Box */}
            <div className="mt-12 rounded-md border border-slate-200 bg-slate-50/60 p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#010b27] text-white font-serif font-bold text-lg">
                  {article.author ? article.author.charAt(0) : 'S'}
                </div>
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#fd7302]">
                    Om forfatteren
                  </span>
                  <h4 className="font-serif text-lg font-bold text-[#010b27]">
                    {article.author || 'Skatunderlup Redaktion'}
                  </h4>
                  <p className="mt-1 text-xs sm:text-sm text-slate-600 leading-relaxed">
                    {article.author === 'Martin Krogh'
                      ? 'Martin Krogh er skattefaglig rådgiver med speciale i skattetilsyn, klagesager og skatteretlige tvister for erhvervsdrivende og private.'
                      : 'Skatunderlups redaktion dækker skatteret, afgiftslove og regnskabsmæssige vilkår med fokus på retssikkerhed og saglig formidling.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Comment section architectural preparation */}
            <div className="mt-8 rounded-md border border-dashed border-slate-200 bg-slate-50/50 p-4 text-center">
              <div className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-600">
                <i className="fa-regular fa-comments text-sm text-[#fd7302]" aria-hidden="true" />
                <span>Læserdebat & kommentarer</span>
              </div>
              <p className="mt-1 text-xs text-slate-400">
                Debatsporet er midlertidigt forbeholdt verificerede faglige indlæg og redaktionelt modererede kommentarer.
              </p>
            </div>
          </div>

          {/* Sticky Sidebar with Fakta & Rådgivningshenvisning */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
            {/* Hurtige Fakta Card */}
            <div className="rounded-md border border-slate-200 bg-white p-5 shadow-xs">
              <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-4">
                <i className="fa-solid fa-circle-info text-[#fd7302]" aria-hidden="true" />
                <h3 className="font-serif text-base font-bold text-[#010b27]">
                  Fakta om artiklen
                </h3>
              </div>

              <dl className="space-y-3 text-xs">
                <div className="flex items-center justify-between">
                  <dt className="text-slate-500">Målgruppe</dt>
                  <dd className="font-semibold text-[#010b27] capitalize">
                    {article.audience === 'erhverv'
                      ? 'Erhvervsdrivende'
                      : article.audience === 'privat'
                      ? 'Private borgere'
                      : 'Erhverv & Private'}
                  </dd>
                </div>

                <div className="flex items-center justify-between">
                  <dt className="text-slate-500">Kategori</dt>
                  <dd className="font-semibold text-[#010b27]">{categoryName}</dd>
                </div>

                <div className="flex items-center justify-between">
                  <dt className="text-slate-500">Læsetid</dt>
                  <dd className="font-semibold text-[#010b27]">{article.reading_time || 'Ca. 5 min'}</dd>
                </div>

                <div className="flex items-center justify-between">
                  <dt className="text-slate-500">Dato</dt>
                  <dd className="font-semibold text-[#010b27]">{formattedDate}</dd>
                </div>
              </dl>

              <div className="mt-5 pt-4 border-t border-slate-100">
                <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                  Del eller videresend
                </div>
                <ShareMenu
                  title={article.title}
                  subtitle={article.subtitle}
                  url={typeof window !== 'undefined' ? window.location.href : ''}
                />
              </div>
            </div>

            {/* Rådgivnings-boks i sidebaren */}
            <div className="rounded-md border border-slate-200 bg-slate-50/70 p-5 shadow-xs">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#fd7302]">
                Konkret skattesag?
              </span>
              <h4 className="mt-1 font-serif text-base font-bold text-[#010b27]">
                Har du brug for juridisk sparring?
              </h4>
              <p className="mt-2 text-xs text-slate-600 leading-relaxed">
                Skatunderlup er et uafhængigt nyhedsmedie. Har du modtaget et agterbrev eller varsel om kontrol, henviser vi til specialiseret rådgivning hos Martin Krogh.
              </p>
              <Link
                to="/kontakt"
                className="mt-4 inline-flex items-center justify-center w-full gap-2 rounded-xs bg-[#fd7302] hover:bg-[#e06500] px-3.5 py-2 text-xs font-bold text-white transition-colors"
              >
                <span>Gå til kontaktformular</span>
                <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
              </Link>
            </div>

            {/* Tip redaktionen */}
            <div className="rounded-md border border-dashed border-slate-200 bg-white p-4 text-center">
              <p className="text-xs text-slate-600">
                Ligger du inde med oplysninger eller dokumentation om en skattesag?
              </p>
              <Link
                to="/kontakt"
                className="mt-2 inline-block text-xs font-bold text-[#fd7302] hover:underline"
              >
                Tip redaktionen fortroligt →
              </Link>
            </div>
          </aside>
        </div>

        {/* Editorial Help Banner */}
        <div className="mt-12">
          <HelpNotice variant="banner" />
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className="mt-16 pt-10 border-t border-slate-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-2xl font-bold text-[#010b27]">
                Relaterede artikler
              </h3>
              <Link
                to="/artikler"
                className="text-xs font-bold uppercase tracking-wider text-[#fd7302] hover:underline"
              >
                Gå til arkivet →
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {relatedArticles.map((rel) => (
                <ArticleCard key={rel.id} article={rel} variant="secondary" />
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
